import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw, Save } from "lucide-react";
import { api } from "../../api";
import { useApp } from "../../store";
import { SECTION_BACKGROUNDS, FONT_OPTIONS, SCALE_OPTIONS, THEME_DEFAULTS, THEME_PRESETS } from "../../theme";

const selectCls =
  "w-full bg-white/[0.05] border border-white/15 focus:border-[var(--accent)] focus:outline-none px-3 py-2.5 text-sm transition-colors duration-300";

export const DesignPanel = () => {
  const { t, lang, content, setContent } = useApp();
  const [vals, setVals] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const v = {};
    SECTION_BACKGROUNDS.forEach((s) => {
      v[`bg_${s.id}`] = content[`theme_bg_${s.id}`]?.fr || s.def;
    });
    Object.keys(THEME_DEFAULTS).forEach((k) => {
      v[k] = content[`theme_${k}`]?.fr || THEME_DEFAULTS[k];
    });
    setVals(v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!vals) return null;

  const set = (k) => (e) => setVals({ ...vals, [k]: e.target.value });

  const persist = async (values) => {
    setSaving(true);
    try {
      await Promise.all(
        Object.entries(values).map(([k, v]) => api.put(`/content/theme_${k}`, { fr: String(v), en: String(v) }))
      );
      setContent((prev) => {
        const next = { ...prev };
        Object.entries(values).forEach(([k, v]) => {
          next[`theme_${k}`] = { fr: String(v), en: String(v) };
        });
        return next;
      });
      toast.success(t("admin_saved"));
    } catch {
      toast.error(t("admin_save_error"));
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = (preset) => {
    setVals({ ...preset.values });
    persist({ ...preset.values });
  };

  const reset = () => {
    const v = {};
    SECTION_BACKGROUNDS.forEach((s) => (v[`bg_${s.id}`] = s.def));
    Object.assign(v, THEME_DEFAULTS);
    setVals(v);
    persist(v);
  };

  return (
    <div data-testid="admin-design-panel" className="flex flex-col gap-10">
      <p className="text-xs text-white/40 max-w-2xl leading-relaxed">{t("design_hint")}</p>

      <section>
        <h2 className="font-display font-semibold text-2xl mb-6 flex items-center gap-3">
          <span className="w-6 h-px bg-[var(--accent)]" />
          {t("design_presets")}
        </h2>
        <div className="grid sm:grid-cols-3 gap-3 max-w-3xl">
          {THEME_PRESETS.map((p) => (
            <button
              key={p.id}
              data-testid={`design-preset-${p.id}`}
              onClick={() => applyPreset(p)}
              className="border border-white/10 hover:border-[var(--accent)] bg-white/[0.02] p-4 text-left transition-colors duration-300"
            >
              <div className="flex gap-1.5 mb-3">
                {p.swatches.map((c) => (
                  <span key={c} className="w-6 h-6 rounded-full border border-white/15" style={{ background: c }} />
                ))}
              </div>
              <p className="text-sm font-medium">{lang === "fr" ? p.fr : p.en}</p>
            </button>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-display font-semibold text-2xl mb-6 flex items-center gap-3">
          <span className="w-6 h-px bg-[var(--accent)]" />
          {t("design_sections")}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {SECTION_BACKGROUNDS.map((s) => (
            <div key={s.id} data-testid={`design-bg-${s.id}`} className="border border-white/10 bg-white/[0.02] p-4 flex items-center gap-4">
              <input
                type="color"
                data-testid={`design-bg-${s.id}-picker`}
                value={vals[`bg_${s.id}`]}
                onChange={set(`bg_${s.id}`)}
                className="w-11 h-11 shrink-0 cursor-pointer bg-transparent border border-white/15 p-1"
              />
              <div className="min-w-0">
                <p className="text-sm">{lang === "fr" ? s.fr : s.en}</p>
                <p className="font-mono2 text-[11px] text-white/40 uppercase">{vals[`bg_${s.id}`]}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-display font-semibold text-2xl mb-6 flex items-center gap-3">
          <span className="w-6 h-px bg-[var(--accent)]" />
          {t("design_text")}
        </h2>
        <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
          <div className="border border-white/10 bg-white/[0.02] p-4 flex items-center gap-4" data-testid="design-text-color">
            <input
              type="color"
              data-testid="design-text-color-picker"
              value={vals.text_color}
              onChange={set("text_color")}
              className="w-11 h-11 shrink-0 cursor-pointer bg-transparent border border-white/15 p-1"
            />
            <div>
              <p className="text-sm">{t("design_text_color")}</p>
              <p className="font-mono2 text-[11px] text-white/40 uppercase">{vals.text_color}</p>
            </div>
          </div>
          <div className="border border-white/10 bg-white/[0.02] p-4 flex items-center gap-4" data-testid="design-accent">
            <input
              type="color"
              data-testid="design-accent-picker"
              value={vals.accent}
              onChange={set("accent")}
              className="w-11 h-11 shrink-0 cursor-pointer bg-transparent border border-white/15 p-1"
            />
            <div className="min-w-0">
              <p className="text-sm">{t("design_accent")}</p>
              <p className="font-mono2 text-[11px] text-white/40 uppercase">{vals.accent}</p>
              <span className="mt-2 inline-block text-[11px] font-semibold text-white px-3 py-1 rounded-full" style={{ background: vals.accent }}>
                {t("cta_start")}
              </span>
            </div>
          </div>
          <div className="border border-white/10 bg-white/[0.02] p-4" data-testid="design-font-scale">
            <label className="block text-sm mb-2">{t("design_font_scale")}</label>
            <select data-testid="design-font-scale-select" value={vals.font_scale} onChange={set("font_scale")} className={selectCls}>
              {SCALE_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>
                  {lang === "fr" ? o.fr : o.en}
                </option>
              ))}
            </select>
          </div>
          <div className="border border-white/10 bg-white/[0.02] p-4" data-testid="design-font-title">
            <label className="block text-sm mb-2">{t("design_font_title")}</label>
            <select data-testid="design-font-title-select" value={vals.font_title} onChange={set("font_title")} className={selectCls}>
              {FONT_OPTIONS.map((o) => (
                <option key={o.id} value={o.value} style={{ fontFamily: o.value }}>
                  {lang === "fr" ? o.fr : o.en}
                </option>
              ))}
            </select>
            <p className="mt-3 text-xl" style={{ fontFamily: vals.font_title }}>
              Transformer vos projets
            </p>
          </div>
          <div className="border border-white/10 bg-white/[0.02] p-4" data-testid="design-font-body">
            <label className="block text-sm mb-2">{t("design_font_body")}</label>
            <select data-testid="design-font-body-select" value={vals.font_body} onChange={set("font_body")} className={selectCls}>
              {FONT_OPTIONS.map((o) => (
                <option key={o.id} value={o.value} style={{ fontFamily: o.value }}>
                  {lang === "fr" ? o.fr : o.en}
                </option>
              ))}
            </select>
            <p className="mt-3 text-sm" style={{ fontFamily: vals.font_body }}>
              Cabinet de conseil stratégique basé à Papeete, Polynésie française.
            </p>
          </div>
        </div>
      </section>

      <div className="flex flex-wrap gap-3 sticky bottom-4">
        <button
          data-testid="design-save-button"
          onClick={() => persist(vals)}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-60 text-white font-semibold px-6 py-3.5 rounded-full transition-colors duration-300 shadow-lg"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? t("admin_saving") : t("design_save")}
        </button>
        <button
          data-testid="design-reset-button"
          onClick={reset}
          disabled={saving}
          className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 px-6 py-3.5 rounded-full text-sm transition-colors duration-300 bg-[#0B132B]"
        >
          <RotateCcw size={15} />
          {t("design_reset")}
        </button>
      </div>
    </div>
  );
};
